import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { Button } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ClientSessionService } from '@brighthub/uisession';
import { ClientDocumentsStateService } from './client-documents-state.service';
import { ClientDocument, ClientDocumentsState, DocumentCategory } from './client-documents.model';
import { ColResizeDirective } from '../client-actions/col-resize.directive';

export type DocumentSortField = 'fileName' | 'uploadedAt' | 'fileSize' | 'category';
export type DocumentSortDir = 'asc' | 'desc';

const CATEGORY_LABELS: Record<DocumentCategory, string> = {
  'helping-hands': 'Helping Hands',
  mpap:            'MPAP',
  equifax:         'Equifax',
  'user-upload':   'User Upload',
  other:           'Other',
};

const CATEGORY_SEVERITIES: Record<DocumentCategory, string> = {
  'helping-hands': 'info',
  mpap:            'success',
  equifax:         'warn',
  'user-upload':   'secondary',
  other:           'contrast',
};

const FILE_ICONS: Record<string, string> = {
  pdf:  'pi-file-pdf',
  jpg:  'pi-image',
  jpeg: 'pi-image',
  png:  'pi-image',
  doc:  'pi-file-word',
  docx: 'pi-file-word',
  xls:  'pi-file-excel',
  xlsx: 'pi-file-excel',
  zip:  'pi-file',
};

@Component({
  selector: 'bai-client-documents',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, SkeletonModule, TableModule, TagModule, Button, TooltipModule, ColResizeDirective],
  templateUrl: './client-documents.component.html',
  styleUrl: './client-documents.component.scss',
})
export class ClientDocumentsComponent {
  private readonly _session = inject(ClientSessionService);
  private readonly _stateService = inject(ClientDocumentsStateService);

  readonly loading = signal(true);

  private readonly _sessionId = computed(
    () => this._session.selectedSession()?.uiSessionId ?? null
  );

  constructor() {
    effect(() => {
      const id = this._sessionId();
      if (id) {
        this._stateService.ensureState(id);
        this.loading.set(false);
      } else {
        this.loading.set(false);
      }
    });
  }

  readonly state = computed((): ClientDocumentsState | null => {
    const id = this._sessionId();
    if (!id) return null;
    return this._stateService.storeSnapshot()[id] ?? null;
  });

  // ---------------------------------------------------------------------------
  // Filter / sort / search signals
  // ---------------------------------------------------------------------------

  readonly searchQuery = signal('');
  readonly selectedCategory = signal<DocumentCategory | 'all'>('all');
  readonly selectedFileType = signal<string | 'all'>('all');
  readonly sortField = signal<DocumentSortField>('uploadedAt');
  readonly sortDir = signal<DocumentSortDir>('desc');

  // ---------------------------------------------------------------------------
  // Derived: filtered + sorted documents
  // ---------------------------------------------------------------------------

  readonly filteredDocuments = computed((): ClientDocument[] => {
    const s = this.state();
    if (!s) return [];

    const q = this.searchQuery().toLowerCase().trim();
    const cat = this.selectedCategory();
    const ft = this.selectedFileType();
    const field = this.sortField();
    const dir = this.sortDir();

    let items = [...s.documents];

    if (cat !== 'all') {
      items = items.filter(d => d.category === cat);
    }

    if (ft !== 'all') {
      items = items.filter(d => d.fileType.toLowerCase() === ft.toLowerCase());
    }

    if (q) {
      items = items.filter(d =>
        d.fileName.toLowerCase().includes(q) ||
        d.uploadedBy.toLowerCase().includes(q) ||
        d.fileType.toLowerCase().includes(q) ||
        CATEGORY_LABELS[d.category].toLowerCase().includes(q)
      );
    }

    items.sort((a, b) => {
      let cmp = 0;
      if (field === 'fileName')    cmp = a.fileName.localeCompare(b.fileName);
      else if (field === 'uploadedAt') cmp = a.uploadedAt.localeCompare(b.uploadedAt);
      else if (field === 'fileSize')   cmp = a.fileSize - b.fileSize;
      else if (field === 'category')   cmp = a.category.localeCompare(b.category);
      return dir === 'desc' ? -cmp : cmp;
    });

    return items;
  });

  readonly uniqueFileTypes = computed((): string[] => {
    const s = this.state();
    if (!s) return [];
    const types = new Set(s.documents.map(d => d.fileType.toLowerCase()));
    return Array.from(types).sort();
  });

  // ---------------------------------------------------------------------------
  // Expose constants to template
  // ---------------------------------------------------------------------------

  readonly categories: (DocumentCategory | 'all')[] = [
    'all', 'helping-hands', 'mpap', 'equifax', 'user-upload', 'other',
  ];

  readonly sortFields: { value: DocumentSortField; label: string }[] = [
    { value: 'uploadedAt', label: 'Upload Date' },
    { value: 'fileName',   label: 'File Name' },
    { value: 'fileSize',   label: 'File Size' },
    { value: 'category',   label: 'Category' },
  ];

  categoryLabel(cat: DocumentCategory | 'all'): string {
    return cat === 'all' ? 'All Categories' : CATEGORY_LABELS[cat];
  }

  getCategoryLabel(cat: DocumentCategory): string {
    return CATEGORY_LABELS[cat];
  }

  getCategorySeverity(cat: DocumentCategory): string {
    return CATEGORY_SEVERITIES[cat];
  }

  getFileIcon(fileType: string): string {
    return FILE_ICONS[fileType.toLowerCase()] ?? 'pi-file';
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  formatDate(iso: string): string {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(iso));
  }

  toggleSort(field: DocumentSortField): void {
    if (this.sortField() === field) {
      this.sortDir.set(this.sortDir() === 'desc' ? 'asc' : 'desc');
    } else {
      this.sortField.set(field);
      this.sortDir.set('desc');
    }
  }

  sortIcon(field: DocumentSortField): string {
    if (this.sortField() !== field) return 'pi pi-sort';
    return this.sortDir() === 'desc' ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up';
  }

  // ---------------------------------------------------------------------------
  // Track-by helpers
  // ---------------------------------------------------------------------------

  trackDocument(_: number, d: ClientDocument): string { return d.id; }
}
